// Server-side reproduction of the query components/Listing/Listing.js issues
// on first load — page 1, no visitor-selected filters.
//
// Listing is a client component and fetches in a useEffect, so the server has
// no copy of the events it will show. Anything the server says about that list
// (the category page's ItemList) has to ask the API the same question the
// client will, or it describes a different list than visitors see.
//
// KEEP IN SYNC with Listing.js: `safeConfig`, `pageCategory`,
// `defaultSortValue`, `mapSortFilterValue`, and the scoped branch of
// `fetchData`. Listing.js is "use client", so a server module can't import
// those functions from it directly.

const normalizeFilterToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[₹$€]/g, "")
    .replace(/\brs\.?\b/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const SORT_MAP = {
  latest: "latest",
  date: "latest",
  by_date: "latest",
  event_date_asc: "latest",
  event_date: "latest",
  popularity: "popularity",
  price_low_high: "price_low_high",
  by_lowest_price: "price_low_high",
  lowest_price: "price_low_high",
  price_low_to_high: "price_low_high",
  price_high_low: "price_high_low",
  by_highest_price: "price_high_low",
  highest_price: "price_high_low",
  price_high_to_low: "price_high_low",
};

// Unmapped values pass through unchanged, exactly as Listing.js does — a CMS
// sort value outside this table is still sent to the API verbatim.
const mapSortFilterValue = (value) => {
  if (!value) return "";
  const key = normalizeFilterToken(value).replace(/\s+/g, "_");
  return SORT_MAP[key] || value;
};

/**
 * @returns {{ params: object, pageCategory: string|undefined } }
 */
export function buildScopedListingParams({ config = {}, city }) {
  const nestedConfig = config?.["1"]?.data || {};
  const safeConfig = { ...config, ...nestedConfig };

  const pageCategory =
    safeConfig.filter_category?.value ||
    safeConfig.filter_category?.key ||
    safeConfig.category;

  const allowSort =
    Boolean(safeConfig.allow_sort) && safeConfig.allow_sort !== "false";
  const cmsSortOptions =
    allowSort &&
    Array.isArray(safeConfig.sort_options) &&
    safeConfig.sort_options.length > 0
      ? safeConfig.sort_options
      : [];
  const defaultSortValue =
    safeConfig.sort ||
    safeConfig.default_sort ||
    (cmsSortOptions.length > 0 ? cmsSortOptions[0]?.value || "" : "");

  const mappedSort = mapSortFilterValue(defaultSortValue);

  const params = {
    limit: safeConfig.limit || 12,
    page: 1,
    ...(mappedSort ? { "filters[sort_by]": mappedSort } : {}),
    "filters[city]": city,
  };

  if (safeConfig.filter_by) {
    params["filters[tag]"] = safeConfig.filter_by;
  }

  if (pageCategory) {
    params["filters[category_slug]"] = pageCategory;
  }

  return { params, pageCategory };
}

/**
 * Fills in the category a city-category listing is scoped to when the CMS
 * doesn't set one. Rows such as `/{city}/navratri-2026` carry neither
 * `filter_category` nor `category`, so the listing ran unscoped and showed
 * every event in the city under a "Navratri 2026" heading. The row's own
 * category, or failing that the page's category slug, is what the page is
 * about.
 *
 * Used by CmsRenderer (the listing and its ItemList) and generateMetadata
 * (the zero-events noindex check) so all three ask the API the same question.
 */
export function resolveListingConfig(config = {}, { listingType, cmsEntry, slugs } = {}) {
  if (listingType !== "category") return config;
  const nestedConfig = config?.["1"]?.data || {};
  const safeConfig = { ...config, ...nestedConfig };
  if (
    safeConfig.filter_category?.value ||
    safeConfig.filter_category?.key ||
    safeConfig.category
  ) {
    return config;
  }
  const fromEntry = String(cmsEntry?.category?.slug || "").replace(/^category\//, "");
  const segments = Array.isArray(slugs) ? slugs.filter(Boolean) : [];
  const fromUrl = segments.length === 2 ? segments[1].toLowerCase() : "";
  const category = fromEntry || fromUrl;
  return category ? { ...config, category } : config;
}

/**
 * The query Listing.js falls back to when the scoped page-1 query is empty:
 * no city, tag or category, so visitors get generic events with a note.
 */
export function buildFallbackListingParams({ config = {} }) {
  const { params } = buildScopedListingParams({ config, city: "" });
  const {
    "filters[city]": _city,
    "filters[tag]": _tag,
    "filters[category_slug]": _category,
    "filters[sort_by]": _sortBy,
    ...rest
  } = params;
  return rest;
}

/**
 * Sorts an array of events by date, price, or popularity.
 */
export function sortEventsList(events, sortType = "latest") {
  if (!Array.isArray(events) || events.length <= 1) return events;
  const mappedSort = mapSortFilterValue(sortType);
  const sorted = [...events];
  switch (mappedSort) {
    case "price_low_high":
      return sorted.sort(
        (a, b) =>
          (Number(
            a.amount ??
              (typeof a.price === "string"
                ? a.price.replace(/[^0-9.]/g, "")
                : a.price),
          ) || 0) -
          (Number(
            b.amount ??
              (typeof b.price === "string"
                ? b.price.replace(/[^0-9.]/g, "")
                : b.price),
          ) || 0),
      );
    case "price_high_low":
      return sorted.sort(
        (a, b) =>
          (Number(
            b.amount ??
              (typeof b.price === "string"
                ? b.price.replace(/[^0-9.]/g, "")
                : b.price),
          ) || 0) -
          (Number(
            a.amount ??
              (typeof a.price === "string"
                ? a.price.replace(/[^0-9.]/g, "")
                : a.price),
          ) || 0),
      );
    case "popularity":
      return sorted.sort(
        (a, b) =>
          (Number(b.show_available_tickets) || 0) -
          (Number(a.show_available_tickets) || 0),
      );
    case "latest":
    default:
      return sorted.sort((a, b) => {
        const timeA =
          Date.parse((a.start_date || "") + "T" + (a.start_time || "00:00:00")) ||
          0;
        const timeB =
          Date.parse((b.start_date || "") + "T" + (b.start_time || "00:00:00")) ||
          0;
        return timeA - timeB;
      });
  }
}
